import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { UntypedFormGroup, Validators } from '@angular/forms';
import {
  ControlPlateType,
  ParkingProcessInfoDTO,
  ParkingProcessLimitedZoneWithParentInfoDTO,
  ParkingProcessSubscriptionAssignmentInfoDTO,
  ParkingProcessZoneInfoDTO,
  UserInfoDTO,
} from '../../base/models/models.swagger.generated';
import { ParkingService } from './parking/parking.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, Observable, of } from 'rxjs';

export interface IPlate {
  countryCode: string;
  type: ControlPlateType;
  numberPlate: string;
}

export class ParkingHelper {

  public clientDropdownValues: {name: string; id: number}[] = [];
  public subscriptionsDropdownValues: {name: string; id: number; assignment: ParkingProcessSubscriptionAssignmentInfoDTO}[] = [];
  public zoneDropdownValues: {name: string[]; id: number}[] = [];
  public parkingSpaceNumberDefinition: string;
  public controlPlateDropdownValues: {name: string; id: number; plate: IPlate}[] = [];

  public parkingProcessZoneInfoDTO: ParkingProcessZoneInfoDTO[];
  public zonesLoaded = new Subject();
  public hasFoundZones = true;
  public hasFoundSubscriptions = true;
  public hasFoundControlPlates = true;

  private user: UserInfoDTO;

  constructor(private isAutomaticParking: boolean,
    private parkingProcessInfoService: ParkingService,
    private translate: TranslateService,
    private spinnerService: SpinnerService) {}

  // user needs to be set beforehand
  public assignFormFieldSubscriptions(parkingForm: UntypedFormGroup, subscriptions: Subscription, user: UserInfoDTO) {
    this.user = user;
    subscriptions.add(
      parkingForm.get('clientId').valueChanges
        .pipe(
          filter(val => !!val),
          distinctUntilChanged(),
          switchMap(clientId => this.isAutomaticParking
            ? this.parkingProcessInfoService.getAutomaticParkingScheduleZoneInfos(clientId)
            : this.parkingProcessInfoService.getParkingProcessZoneInfos(clientId)),
        )
        .subscribe(parkingProcessZoneInfoDTO => {
          this.parkingProcessZoneInfoDTO = parkingProcessZoneInfoDTO;
          this.createZoneDropdownValues(parkingProcessZoneInfoDTO);
          this.hasFoundZones = this.zoneDropdownValues?.length > 0;
          const zoneValue = this.zoneDropdownValues?.find(z => z.id === parkingForm.get('zoneId').getRawValue());
          if (zoneValue == null) {
            if (this.zoneDropdownValues?.length === 1) {
              parkingForm.get('zoneId').setValue(this.zoneDropdownValues[0].id, {onlySelf: true});
              parkingForm.get('zoneId').disable();
            } else {
              parkingForm.get('zoneId').setValue(null, {onlySelf: true});
              parkingForm.get('zoneId').enable();
            }
          }
        })
    );

    subscriptions.add(
      parkingForm.get('zoneId').valueChanges.pipe(distinctUntilChanged()).subscribe(zoneId => {
        this.createSubscriptionDropdownValues(zoneId);
        if (zoneId) {
          this.hasFoundSubscriptions = this.subscriptionsDropdownValues?.length > 0;
        }
        const subscription = this.subscriptionsDropdownValues?.find(s => s.id === parkingForm.get('subscriptionId').getRawValue());
        if (subscription == null) {
          if (this.subscriptionsDropdownValues?.length === 1) {
            parkingForm.get('subscriptionId').setValue(this.subscriptionsDropdownValues[0].id, {onlySelf: true});
            parkingForm.get('subscriptionId').disable();
          } else {
            parkingForm.get('subscriptionId').setValue(null, {onlySelf: true});
            parkingForm.get('subscriptionId').enable();
          }
        }
      })
    );

    subscriptions.add(
      parkingForm.get('subscriptionId').valueChanges.pipe(distinctUntilChanged()).subscribe(subscriptionId => {
        this.handleVehicleFields(this.user, subscriptionId, parkingForm.get('zoneId').value);
        if (subscriptionId) {
          this.hasFoundControlPlates = this.controlPlateDropdownValues?.length > 0;
        }
        if (this.controlPlateDropdownValues?.length === 1) {
          parkingForm.get('controlPlateId').setValue(this.controlPlateDropdownValues[0].id, {onlySelf: true});
          parkingForm.get('controlPlateId').disable();
        } else {
          parkingForm.get('controlPlateId').enable();
        }
      })
    );

    if (parkingForm.get('newVehicle')) {
      subscriptions.add(
        parkingForm.get('controlPlateId').valueChanges.subscribe(controlPlateId => {
          if (controlPlateId === -1) {
            parkingForm.get('newVehicle').get('type').setValidators([Validators.required]);
            parkingForm.get('newVehicle').get('type').setValue(null);
            parkingForm.get('newVehicle').get('countryCode').setValidators([Validators.required]);
            parkingForm.get('newVehicle').get('countryCode').setValue(null);
            parkingForm.get('newVehicle').get('numberPlate').setValidators([Validators.required]);
            parkingForm.get('newVehicle').get('numberPlate').setValue(null);
          } else {
            const selectedPlateEntry = this.controlPlateDropdownValues.find(plate => plate.id === controlPlateId);
            if (selectedPlateEntry) {
              parkingForm.get('newVehicle').get('type').setValidators([]);
              parkingForm.get('newVehicle').get('type').setValue(selectedPlateEntry.plate.type);
              parkingForm.get('newVehicle').get('countryCode').setValidators([]);
              parkingForm.get('newVehicle').get('countryCode').setValue(selectedPlateEntry.plate.countryCode);
              parkingForm.get('newVehicle').get('numberPlate').setValidators([]);
              parkingForm.get('newVehicle').get('numberPlate').setValue(selectedPlateEntry.plate.numberPlate);
            }
          }
          parkingForm.get('newVehicle').get('type').updateValueAndValidity();
          parkingForm.get('newVehicle').get('countryCode').updateValueAndValidity();
          parkingForm.get('newVehicle').get('numberPlate').updateValueAndValidity();
        })
      );
    }

    if(!this.isAutomaticParking) {
      subscriptions.add(
        this.translate.onLangChange.subscribe(() => {
          if (this.controlPlateDropdownValues.find(v => v.id === -1)) {
            this.controlPlateDropdownValues = this.controlPlateDropdownValues.map(v => {
              if (v.id === -1) {
                return {name: this.translate.instant('i18n.domain.parking.addVehicle') + '...', id: -1, plate: null};
              } else {
                return v;
              }
            });
          } else {
            this.controlPlateDropdownValues = [...this.controlPlateDropdownValues, {
              name: this.translate.instant('i18n.domain.parking.addVehicle') + '...',
              id: -1,
              plate: null
            }];
          }
        })
      );
    }
  }

  public handleParkingProcessInfo(user: UserInfoDTO, parkingProcessInfo: ParkingProcessInfoDTO, parkingForm: UntypedFormGroup) {
    if (!parkingProcessInfo?.activeCheckinInfo) {
      // TODO: call reset
      return;
    }
    this.user = user;
    const activeCheckinInfo = parkingProcessInfo.activeCheckinInfo;
    this.createClientDropdownValues();
    parkingForm.get('clientId').setValue(activeCheckinInfo.clientId);

    this.zoneDropdownValues = [{name: this.getZoneNameArray(activeCheckinInfo.zone), id: activeCheckinInfo.zoneId}];
    parkingForm.get('zoneId').setValue(activeCheckinInfo.zoneId);

    this.subscriptionsDropdownValues = [{
      name: activeCheckinInfo.subscriptionAssignment?.subscription.name ?? this.translate.instant('i18n.domain.parking.no-subscription-assigned'),
      id: activeCheckinInfo.subscriptionAssignment?.id ?? -1,
      assignment: null
    }];
    parkingForm.get('subscriptionId').setValue(activeCheckinInfo.subscriptionAssignment?.id ?? -1);

    this.controlPlateDropdownValues = [{name: activeCheckinInfo.controlPlateNumber, id: 1, plate: null}];
    parkingForm.get('controlPlateId').setValue(1);

    parkingForm.get('parkingSpaceNumber').setValue(activeCheckinInfo.parkingSpaceNumber);
  }

  public initDropDownValuesForValidForm(user: UserInfoDTO, parkingForm: UntypedFormGroup): Observable<ParkingProcessZoneInfoDTO[]> {
    this.hasFoundZones = true;
    this.hasFoundSubscriptions = true;
    this.hasFoundControlPlates = true;
    this.user = user;
    this.createClientDropdownValues();
    if (parkingForm.get('clientId').value) {
      this.spinnerService.showSpinner = true;
      return this.parkingProcessInfoService.getAutomaticParkingScheduleZoneInfos(parkingForm.get('clientId').value).pipe(tap({
        next: parkingProcessZoneInfo => {
          this.spinnerService.reset();
          this.parkingProcessZoneInfoDTO = parkingProcessZoneInfo;
            this.createZoneDropdownValues(parkingProcessZoneInfo);
            if (this.zoneDropdownValues?.length === 1) {
              parkingForm.get('zoneId').disable();
            } else {
              parkingForm.get('zoneId').enable();
            }
            this.createSubscriptionDropdownValues(parkingForm.get('zoneId').value);
            if (this.subscriptionsDropdownValues?.length === 1) {
              parkingForm.get('subscriptionId').disable();
            } else {
              parkingForm.get('subscriptionId').enable();
            }
            this.handleVehicleFields(user, parkingForm.get('subscriptionId').value, parkingForm.get('zoneId').value);
            if (this.controlPlateDropdownValues?.length === 1) {
              parkingForm.get('controlPlateId').disable();
            } else {
              parkingForm.get('controlPlateId').enable();
            }
          },
        error: error => {this.spinnerService.reset();}}));
    }
    return of(null);
  }

  public createParkingProcessDropdownFieldsValues(user: UserInfoDTO): void {
    this.hasFoundZones = true;
    this.hasFoundSubscriptions = true;
    this.hasFoundControlPlates = true;
    this.user = user;
    this.createClientDropdownValues();
  }

  public handleVehicleFields(user: UserInfoDTO, subscriptionId: number, zoneId: number): void {
    const assignment = this.subscriptionsDropdownValues?.find(s => s.id === subscriptionId)?.assignment;
    const zoneInfo = this.parkingProcessZoneInfoDTO?.find(z => z.id === zoneId);
    if (!assignment || !zoneInfo) {
      this.controlPlateDropdownValues = [];
      return;
    }
    // TODO: switch if statement for V1.1 to support when foreign parkers are allowed
    // if (this.isAutomaticParking || !zoneInfo.foreignParkersAllowed) {
    if (true) {
      const linkedParkerTags = user.linkedParkers
        .find(parker => parker.parkerId === assignment.parkerId)?.tags;
      this.controlPlateDropdownValues = linkedParkerTags.map(tag => ({
        name: tag.controlPlateNumber?.toUpperCase(),
        id: tag.id,
        plate: {
          countryCode: tag.countryCode,
          type: tag.controlPlateType,
          numberPlate: tag.controlPlateNumber
        }
      }));
    } else {
      this.controlPlateDropdownValues = user.controlPlates.map(plate => ({
        name: plate.controlPlateNumber?.toUpperCase(),
        id: plate.id,
        plate: {
          countryCode: plate.countryCode,
          type: plate.controlPlateType,
          numberPlate: plate.controlPlateNumber
        }
      }));

      this.controlPlateDropdownValues = [...this.controlPlateDropdownValues, {
        name: this.translate.instant('i18n.domain.parking.addVehicle') + '...',
        id: -1,
        plate: null
      }];
    }
  }

  public resetZone(form: UntypedFormGroup): void {
    form.get('zoneId').setValue(null);
    this.resetSubscription(form);
  }

  public resetSubscription(form: UntypedFormGroup): void {
    form.get('subscriptionId').setValue(null);
    this.resetWeekdays(form);
  }

  public resetWeekdays(form: UntypedFormGroup): void {
    if (!!form.get('weekdays')) {
      form.get('weekdays').get('allowedMonday').setValue(false);
      form.get('weekdays').get('allowedTuesday').setValue(false);
      form.get('weekdays').get('allowedWednesday').setValue(false);
      form.get('weekdays').get('allowedThursday').setValue(false);
      form.get('weekdays').get('allowedFriday').setValue(false);
      form.get('weekdays').get('allowedSaturday').setValue(false);
      form.get('weekdays').get('allowedSunday').setValue(false);
      this.resetTimeDuration(form);
    }
  }

  public resetTimeDuration(form: UntypedFormGroup): void {
    form.get('startTime').setValue(null);
    form.get('duration').setValue(null);
  }

  private getZoneNameArray(zone: ParkingProcessZoneInfoDTO | ParkingProcessLimitedZoneWithParentInfoDTO, names: string[] = []): string[] {
    names.push(zone.name);
    return zone.parent ? this.getZoneNameArray(zone.parent, names) : names.reverse();
  }

  private createSubscriptionDropdownValues(zoneId: number): void {
    if (!zoneId || !this.parkingProcessZoneInfoDTO) {
      this.subscriptionsDropdownValues = [];
      return;
    }
    const zone = this.parkingProcessZoneInfoDTO.find(z => z.id === zoneId);
    let subscriptionAssignments = [];
    if (zone && zone.subscriptionAssignments) {
      subscriptionAssignments = zone.subscriptionAssignments;
    }

    this.parkingSpaceNumberDefinition = zone?.parkingSpaceNumberDefinition;

    // TODO: commented out code in sort was for AVP-501 -> deactivated again since there were massive differences with mobile app => needs clarification
    this.subscriptionsDropdownValues = subscriptionAssignments.map(s => ({name: s.subscription.name, id: s.id, assignment: s}))
      .sort((o1, o2) => /*o1.assignment.isFavorite != o2.assignment.isFavorite ? o2.assignment.isFavorite - o1.assignment.isFavorite : */o1.name.toLowerCase().localeCompare(o2.name.toLowerCase()));
  }

  private createZoneDropdownValues(parkingProcessZoneInfoDTO: ParkingProcessZoneInfoDTO[]): void {
    this.zoneDropdownValues = parkingProcessZoneInfoDTO.map(zone => ({
      name: this.getZoneNameArray(zone),
      id: zone.id,
    }))
      .sort((o1, o2) => o1.name.join().toLowerCase().localeCompare(o2.name.join().toLowerCase()));
  }

  private createClientDropdownValues(): void {
    this.clientDropdownValues = this.user.linkedParkers
      .filter(parker => parker.isClientManaged && parker.client?.isActive && (!this.isAutomaticParking || parker.client?.automaticParkingSchedulesEnabled))
      .map(lp => lp.client)
      .map(client => ({name: client.name, id: client.id}))
      .filter((value, index, self) => index === self.findIndex(val => val.id === value.id));
  }
}
